/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import config from '../../../../share/config';
import session from '../../../../share/session';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',
  START_CONFIRMATION: 'startConfirmation',
  SELECT_TRANSMITTER: 'selectTransmitter',
  TEST_TRANSMITTER: 'testTransmitter',
  COMMUNICATION_FAILED: 'communicationFailed',
  FAILED: 'failed'
};

export default {
  name: 'requirement102',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      systemId: '',
      displayInfo: '',
      transmitterList: [],
      selectedTransmitter: undefined,
      modal_requirement_102: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED:
        case PHASE.COMMUNICATION_FAILED:
          return 'danger';
        default:
          return 'secondary';
      }
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_requirement_102 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      // リソース(作業サポート_DID)のi18nキー取得
      this.i18nWsDidKey = `wsDid${this.systemId}`;
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_102 = false;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /**
     * データ読出し
     */
    async read() {
      try {
        this.isLoading = true;
        const token = session.getToken();
        // データ読み出し(要求)
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'standby'
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              this.setTransmitterList(response.data.user_data.transmitter_list);
              this.phase = PHASE.START_CONFIRMATION;
              break;
            default:
              this.phase = PHASE.COMMUNICATION_FAILED;
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },
    /**
     * 発信機選択画面用 項目セット
     */
    setTransmitterList(data) {
      this.transmitterList = data.map(item => {
        return {
          text: this.$t(`${this.i18nWsDidKey}.1191_1_all_${item}`),
          value: item,
        };
      });
      this.transmitterList.sort((a, b) => a.value > b.value ? 1 : -1);
    },

    /**
     * データ書込み
     */
    async write() {
      try {
        this.isLoading = true;
        const token = session.getToken();
        // phaseに応じてリクエストボディを設定
        let reqBody = undefined;
        if(this.phase === PHASE.SELECT_TRANSMITTER){
          // 発信機選択から実行された場合
          reqBody = {
            id_token: token.id_token,
            type: 'test_start',
            selected: this.selectedTransmitter
          };
        } else {
          // 上記以外(発信機テスト)から実行された場合
          reqBody = {
            id_token: token.id_token,
            type: 'test_stop'
          };
        }

        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          reqBody,
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              switch (this.phase) {
                case PHASE.SELECT_TRANSMITTER: {
                  // 選択した発信機名取得
                  const transmitter = this.$t(`${this.i18nWsDidKey}.1191_1_all_${this.selectedTransmitter}`);
                  // 取得した発信機名を置換し、表示文言を設定
                  this.displayInfo = this.$t(`${this.i18nWsKey}.REQ_WS_102_TXT_3`).replace('$USER$', transmitter);
                  this.phase = PHASE.TEST_TRANSMITTER;
                  break;
                }
                case PHASE.TEST_TRANSMITTER:
                  // 発信機選択変数の初期化
                  this.selectedTransmitter = undefined;
                  this.phase = PHASE.SELECT_TRANSMITTER;
                  break;
              }
              break;
            default:
              this.phase = PHASE.COMMUNICATION_FAILED;
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Terminate
     */
    async exit() {
      try {
        this.isLoading = true;
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'exit'
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              this.closeWizard();
              break;
            default:
              if (this.phase === PHASE.FAILED) {
                this.closeWizard();
              } else {
                this.showUnexpectedError();
              }
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 確認表示 OKボタン押下イベント */
    async moveSelectTransmitter() {
      this.phase = PHASE.SELECT_TRANSMITTER;
    },
    /** 発信機選択 OKボタン押下イベント / 発信機テスト 終了ボタン押下イベント */
    async writeEvent() {
      await this.write();
    },
    /** 発信機選択 キャンセル or エラー関連画面 OKボタン押下イベント */
    async exitEvent() {
      await this.exit();
    },

    /** その他エラーの表示 */
    showUnexpectedError() {
      this.phase = PHASE.FAILED;
    },
  },

  async mounted() {
    this.init();
    await this.read();
  },
};